import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ui from '../../styles/ui.module.css';

interface CopyButtonProps {
  value: string;
  label?: string;
  /** A fuller name for screen readers when `label` is short ("Copy" → "Copy status link"). */
  accessibleLabel?: string;
  className?: string;
}

/** Copies `value` to the clipboard and says so for two seconds. */
export default function CopyButton({ value, label, accessibleLabel, className }: CopyButtonProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
  }

  // The label changes for sighted users; the status line says it for screen
  // readers, which don't reliably announce a button renaming itself.
  return (
    <>
      {/* The fuller name only while idle, so "Copied" never sits under a name that doesn't contain it. */}
      <button type="button" className={className ?? ui.buttonSecondary} onClick={copy} aria-label={state === 'idle' ? accessibleLabel : undefined}>
        {state === 'copied' ? t('secret.copied') : state === 'failed' ? t('secret.copyFailed') : (label ?? t('secret.copy'))}
      </button>
      <span role="status" className="visually-hidden">
        {state === 'copied' ? t('secret.copied') : state === 'failed' ? t('secret.copyFailed') : ''}
      </span>
    </>
  );
}
